/**
 * Unit counters drawn into the atlas once per (style, symbol, faction colour, variant, language).
 * Two styles (settings `unitStyle`):
 *  - `nato` — APP-6-inspired symbols (✕ infantry, oval armour, dot artillery …) in a flat frame;
 *  - `illustrated` — die-cut cardboard counters with side-view silhouettes (atlas/unitArt.ts).
 * Geometry mirrors the HUD's SVG counter (ui/common/UnitCounter.tsx): a 60×52 box with a 48×32
 * frame at (6, 12), so map and HUD counters look identical and every overlay (rings, bars,
 * designation, hit box) fits both styles.
 *
 * Counter frames live in the atlas's volatile zone: a style or language switch drops them all
 * and the units layer re-requests what it shows (`Atlas.resetVolatile`, `UnitsLayer.restyle`).
 */
import type { UnitType } from '@friction/engine';
import type { UnitStyle } from '../../app/settings';
import { getLocale, type Locale, tr } from '../../i18n';
import type { Atlas } from '../gfx/atlas';
import { cssColor, darken, lighten, PAL } from '../palette';
import type { Glyph } from './glyphs';
import {
  ART_H,
  ART_SCALE,
  ART_X,
  ART_Y,
  BEVEL_LIGHT,
  BEVEL_SHADE,
  FACE,
  FACE_PATH,
  GRAIN,
  type IllustratedInks,
  illustratedInks,
  KEYLINE_PATH,
  type Tone,
  unitArt,
} from './unitArt';

export type CounterVariant = 'solid' | 'ghost' | 'decoy' | 'exposed';

/** Layout box including a 2-unit margin for the drop shadow. */
export const COUNTER_W = 64;
export const COUNTER_H = 56;
/** Frame centre relative to the box centre (the anchor we place on the map). */
export const FRAME_CX = 30 + 2 - COUNTER_W / 2;
export const FRAME_CY = 28 + 2 - COUNTER_H / 2;
export const FRAME_W = 48;
export const FRAME_H = 32;

/** Atlas pixels per layout unit: the counter's size at the closest zoom on a 2× screen. */
const PX = 2.6;
const INK = cssColor(PAL.ink);

// ---------------------------------------------------------------------------------------------
// NATO
// ---------------------------------------------------------------------------------------------

function natoSymbol(c: CanvasRenderingContext2D, type: UnitType | null, locale: Locale): void {
  c.strokeStyle = INK;
  c.fillStyle = INK;
  c.lineWidth = 1.6;
  c.lineCap = 'square';
  const text = (s: string, size: number, y: number) => {
    c.font = `700 ${size}px "EB Garamond", serif`;
    c.textAlign = 'center';
    c.textBaseline = 'alphabetic';
    c.fillText(s, 30, y);
  };
  switch (type) {
    case 'infantry':
    case 'motorized':
      c.beginPath();
      c.moveTo(6, 12);
      c.lineTo(54, 44);
      c.moveTo(6, 44);
      c.lineTo(54, 12);
      c.stroke();
      if (type === 'motorized') {
        for (const x of [24, 36]) {
          c.beginPath();
          c.arc(x, 39.5, 2.2, 0, Math.PI * 2);
          c.fill();
        }
      }
      break;
    case 'armor':
      c.beginPath();
      c.roundRect(15, 19.5, 30, 17, 8.5);
      c.stroke();
      break;
    case 'artillery':
      c.beginPath();
      c.arc(30, 28, 4.8, 0, Math.PI * 2);
      c.fill();
      break;
    case 'recon':
      c.beginPath();
      c.moveTo(6, 44);
      c.lineTo(54, 12);
      c.stroke();
      break;
    case 'hq':
      text(tr(locale)('unit.symbol.hq'), 15, 33.5);
      break;
    case 'decoy':
      text(tr(locale)('unit.symbol.decoy'), 16, 34);
      break;
    default:
      c.font = '600 20px "EB Garamond", serif';
      c.textAlign = 'center';
      c.fillText('?', 30, 35);
  }
}

function drawNato(
  c: CanvasRenderingContext2D,
  type: UnitType | null,
  color: number,
  variant: CounterVariant,
  locale: Locale,
): void {
  const dashed = variant !== 'solid';
  const fill = cssColor(lighten(color, type === null ? 0.62 : 0.42));
  // soft drop shadow under the frame (not for ghosts: they are memories, not objects)
  if (variant !== 'ghost') {
    c.save();
    c.shadowColor = 'rgba(20,16,10,0.45)';
    c.shadowBlur = 3.2 * PX;
    c.shadowOffsetX = 1.2 * PX;
    c.shadowOffsetY = 1.8 * PX;
    c.fillStyle = fill;
    c.fillRect(6, 12, 48, 32);
    c.restore();
  }
  if (type === 'hq') {
    c.strokeStyle = INK;
    c.lineWidth = 1.6;
    c.beginPath();
    c.moveTo(6, 44);
    c.lineTo(6, 53);
    c.stroke();
  }
  c.fillStyle = fill;
  c.fillRect(6, 12, 48, 32);
  // subtle paper texture inside the frame
  c.fillStyle = 'rgba(255,255,255,0.12)';
  c.fillRect(6, 12, 48, 12);
  c.save();
  c.beginPath();
  c.rect(6, 12, 48, 32);
  c.clip();
  natoSymbol(c, type, locale);
  c.restore();
  c.strokeStyle = cssColor(darken(color, 0.55));
  c.lineWidth = 1.6;
  if (dashed) c.setLineDash([4, 2.5]);
  c.strokeRect(6, 12, 48, 32);
  c.setLineDash([]);
}

// ---------------------------------------------------------------------------------------------
// Illustrated
// ---------------------------------------------------------------------------------------------

const paths = new Map<string, Path2D>();
/** Path2D per path string (built once; the art strings are module constants). */
function path(d: string): Path2D {
  let p = paths.get(d);
  if (!p) {
    p = new Path2D(d);
    paths.set(d, p);
  }
  return p;
}

function toneStyle(tone: Tone, inks: IllustratedInks, face: CanvasGradient): string | CanvasGradient {
  switch (tone) {
    case 'ink':
      return cssColor(inks.ink);
    case 'mid':
      return cssColor(inks.mid);
    case 'hi':
      return cssColor(inks.hi);
    case 'flag':
      return cssColor(inks.flag);
    default:
      return face;
  }
}

/** The silhouette (or "?") in the frame, in art-box coordinates. */
function drawArt(c: CanvasRenderingContext2D, type: UnitType | null, inks: IllustratedInks): void {
  c.save();
  c.translate(ART_X, ART_Y);
  c.scale(ART_SCALE, ART_SCALE);
  // the counter face again, in art coordinates (knock-outs, hollow prints)
  const face = c.createLinearGradient(0, 0, 0, ART_H);
  face.addColorStop(0, cssColor(inks.faceTop));
  face.addColorStop(1, cssColor(inks.faceBottom));
  if (type === null) {
    c.font = '700 62px "EB Garamond", serif';
    c.textAlign = 'center';
    c.textBaseline = 'alphabetic';
    c.fillStyle = cssColor(inks.hi);
    c.globalAlpha = 0.5;
    c.fillText('?', 59, 59);
    c.globalAlpha = 1;
    c.fillStyle = cssColor(inks.ink);
    c.fillText('?', 60, 60);
    c.restore();
    return;
  }
  const art = unitArt(type);
  if (inks.hollow) {
    // canvas and plywood: the outline of what it pretends to be, nothing inside
    const o = path(art.outline);
    c.strokeStyle = cssColor(inks.ink);
    c.lineWidth = 5;
    c.lineJoin = 'round';
    c.stroke(o);
    c.fillStyle = face;
    c.fill(o);
    c.restore();
    return;
  }
  c.lineCap = 'round';
  c.lineJoin = 'round';
  for (const l of art.layers) {
    const style = toneStyle(l.tone, inks, face);
    c.globalAlpha = l.tone === 'hi' ? 0.75 : 1;
    if (l.stroke !== undefined) {
      c.strokeStyle = style;
      c.lineWidth = l.stroke;
      c.stroke(path(l.d));
    } else {
      c.fillStyle = style;
      c.fill(path(l.d), l.evenOdd ? 'evenodd' : 'nonzero');
    }
  }
  c.globalAlpha = 1;
  c.restore();
}

function drawIllustrated(
  c: CanvasRenderingContext2D,
  type: UnitType | null,
  color: number,
  variant: CounterVariant,
): void {
  const inks = illustratedInks(color, variant, type === null);
  const face = path(FACE_PATH);
  const grad = c.createLinearGradient(0, FACE.y, 0, FACE.y + FACE.h);
  grad.addColorStop(0, cssColor(inks.faceTop));
  grad.addColorStop(1, cssColor(inks.faceBottom));
  if (inks.body) {
    // the cardboard's thickness, casting a soft shadow on the map
    c.save();
    c.shadowColor = 'rgba(20,16,10,0.5)';
    c.shadowBlur = 2.8 * PX;
    c.shadowOffsetX = 1 * PX;
    c.shadowOffsetY = 1.6 * PX;
    c.translate(0.55, 0.85);
    c.fillStyle = cssColor(inks.edge);
    c.fill(face);
    c.restore();
  }
  c.fillStyle = grad;
  c.fill(face);
  c.save();
  c.clip(face);
  drawArt(c, type, inks);
  // cardboard grain over face and print
  c.fillStyle = 'rgba(255,250,236,0.2)';
  c.fill(path(GRAIN.light));
  c.fillStyle = 'rgba(20,14,8,0.13)';
  c.fill(path(GRAIN.dark));
  c.strokeStyle = 'rgba(20,14,8,0.08)';
  c.lineWidth = 0.18;
  c.stroke(path(GRAIN.fibre));
  // bevel: the lamp catches the top-left edge
  c.lineCap = 'round';
  c.lineWidth = 0.9;
  c.strokeStyle = 'rgba(255,250,236,0.42)';
  c.stroke(path(BEVEL_LIGHT));
  c.strokeStyle = 'rgba(12,8,4,0.22)';
  c.stroke(path(BEVEL_SHADE));
  c.restore();
  // printed key line, then the die-cut edge
  c.strokeStyle = cssColor(inks.keyline, 0.5);
  c.lineWidth = 0.45;
  c.stroke(path(KEYLINE_PATH));
  c.strokeStyle = cssColor(inks.edge);
  c.lineWidth = inks.dashed ? 1.3 : 0.8;
  if (inks.dashed) c.setLineDash([3.2, 2]);
  c.stroke(face);
  c.setLineDash([]);
}

// ---------------------------------------------------------------------------------------------
// Shared marks
// ---------------------------------------------------------------------------------------------

/** Echelon above the frame: XX (division), XXXX (army HQ). */
function drawEchelon(c: CanvasRenderingContext2D, type: UnitType): void {
  c.font = '700 8.5px "IBM Plex Sans Condensed", sans-serif';
  c.textAlign = 'center';
  c.textBaseline = 'alphabetic';
  c.fillStyle = INK;
  c.lineJoin = 'round';
  c.strokeStyle = 'rgba(243,236,219,0.85)';
  c.lineWidth = 2.2;
  const ech = type === 'hq' ? 'XXXX' : 'XX';
  const spaced = ech.split('').join(' ');
  c.strokeText(spaced, 30, 9.5);
  c.fillText(spaced, 30, 9.5);
}

/** Red DECOY stamp across an unmasked decoy. */
function drawDecoyStamp(c: CanvasRenderingContext2D, locale: Locale): void {
  c.save();
  c.translate(30, 28);
  c.rotate((-8 * Math.PI) / 180);
  c.fillStyle = 'rgba(179,38,30,0.92)';
  c.fillRect(-26, -7, 52, 13);
  c.font = '9px "Special Elite", monospace';
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = '#f5ecd8';
  c.fillText(tr(locale)('unit.decoyStamp'), 0, 0.6);
  c.restore();
}

/**
 * Draws a whole counter in layout units of the 60×52 counter box (origin at its top-left).
 * Exported for the render lab's counter sheet.
 */
export function drawCounter(
  c: CanvasRenderingContext2D,
  style: UnitStyle,
  type: UnitType | null,
  color: number,
  variant: CounterVariant,
  locale: Locale,
): void {
  if (style === 'illustrated') drawIllustrated(c, type, color, variant);
  else drawNato(c, type, color, variant, locale);
  if (type !== null) drawEchelon(c, type);
  if (variant === 'exposed') drawDecoyStamp(c, locale);
}

/**
 * Counter sprite. `type` is what the viewer believes (null = unidentified contact). Lettered in
 * the interface language (the decoy stamp, the decoy letter).
 */
export function counterGlyph(
  atlas: Atlas,
  style: UnitStyle,
  type: UnitType | null,
  color: number,
  variant: CounterVariant,
): Glyph {
  const locale = getLocale();
  const key = `ctr:${style}:${type ?? '?'}:${color}:${variant}:${locale}`;
  const frame = atlas.get(
    key,
    COUNTER_W * PX,
    COUNTER_H * PX,
    (c) => {
      c.scale(PX, PX);
      c.translate(2, 2);
      drawCounter(c, style, type, color, variant, locale);
    },
    true,
  );
  return { frame, w: COUNTER_W, h: COUNTER_H };
}
